import React from "react";
import { Container, Row, Col, Modal } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useTransactions } from "../api/hooks";
import LocalDateTime from "./LocalDateTime";
import ButtonWrapper from "./ButtonWrapper";
import ParkingDuration from "./ParkingDuration";

// Temporary interfaces based on the DTO structure until API client is fixed
interface TransactionEvent {
  createdUtc?: string;
  message?: string;
  type?: string;
}

interface TransactionInvoice {
  id?: string;
  number?: string;
  status?: string;
  url?: string;
}

interface Transaction {
  id: string;
  startUtc?: string;
  endUtc?: string;
  siteName?: string;
  type?: string;
  amount?: number;
  status?: string;
  duration?: number;
  events?: TransactionEvent[];
  invoices?: TransactionInvoice[];
}

const Reports = () => {
  return (
    <Container className="px-5">
      <Row className="gx-2 my-5">
        <Col lg>
          <Transactions />
        </Col>
      </Row>
    </Container>
  );
};

const formatter = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",

  // These options are needed to round to whole numbers if that's what you want.
  //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
  //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
});

const Transactions = () => {
  const { data: transactionsData, isLoading, error } = useTransactions();

  // Cast to expected type until API client is fixed
  const transactions = transactionsData as Transaction[] | undefined;

  const navigate = useNavigate();

  let { id } = useParams();
  let transaction = transactions?.find((t: Transaction) => t.id === id);

  if (isLoading) {
    return (
      <Row>
        <Col>
          <h2>Your Parking Sessions</h2>
          <br />
          <div>Loading transactions...</div>
        </Col>
      </Row>
    );
  }

  if (error) {
    return (
      <Row>
        <Col>
          <h2>Your Parking Sessions</h2>
          <br />
          <div>Error loading transactions: {error.message}</div>
        </Col>
      </Row>
    );
  }

  return (
    <>
      {transaction ? (
        <TransactionDetails />
      ) : (
        <>
          <Row>
            <Col>
              <h2>Your Parking Sessions</h2>
              <br />
            </Col>
          </Row>
          <Row>
            <Col>
              {isLoading && <div>Loading...</div>}

              {transactions && transactions.length > 0 && (
                <div className="table-responsive">
                  <table className="table sharepark-table">
                    <thead>
                      <tr>
                        <th>Start</th>
                        <th>End</th>
                        <th>Duration</th>
                        <th>Site</th>
                        <th>Description</th>
                        <th>Amount</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((t: Transaction, i: number) => {
                        return (
                          <tr
                            key={i}
                            onClick={() => navigate(`/reports/${t.id}`)}
                            className={!t.endUtc ? "table-success" : ""}
                            style={{ cursor: "pointer" }}
                          >
                            <td>
                              {t.startUtc && (
                                <LocalDateTime date={t.startUtc} />
                              )}
                            </td>
                            <td>
                              {t.endUtc ? (
                                <LocalDateTime date={t.endUtc} />
                              ) : (
                                "-"
                              )}
                            </td>
                            <td>
                              {t.endUtc && t.duration ? (
                                <ParkingDuration duration={t.duration} />
                              ) : (
                                <ParkingDuration
                                  duration={
                                    t.startUtc
                                      ? (new Date().getTime() -
                                          new Date(
                                            t.startUtc + "Z"
                                          ).getTime()) /
                                        1000
                                      : 0
                                  }
                                />
                              )}
                            </td>
                            <td>{t.siteName || "-"}</td>
                            <td>{t.type || "-"}</td>
                            <td>
                              {t.amount ? formatter.format(t.amount) : "-"}
                            </td>
                            <td>{t.status || "-"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {transactions && transactions.length === 0 && (
                <div>You have no transactions</div>
              )}
            </Col>
          </Row>
        </>
      )}
    </>
  );
};

const TransactionDetails = () => {
  const { data: transactionsData, isLoading, error } = useTransactions();

  // Cast to expected type until API client is fixed
  const transactions = transactionsData as Transaction[] | undefined;

  const navigate = useNavigate();

  let { id } = useParams();
  let transaction = transactions?.find((t: Transaction) => t.id === id);
  let invoice = transaction?.invoices?.[0];

  if (isLoading) {
    return (
      <Modal show={true} size="xl">
        <Modal.Header style={{ backgroundColor: "white" }}>
          <Col>
            <h2>Transaction Details</h2>
          </Col>
          <Col xs={3} className="text-end">
            <ButtonWrapper
              buttonText="Close x"
              fullWidth={true}
              onClick={() => navigate("/reports")}
            />
          </Col>
        </Modal.Header>
        <Modal.Body>
          <Container>
            <div>Loading transaction details...</div>
          </Container>
        </Modal.Body>
      </Modal>
    );
  }

  if (error) {
    return (
      <Modal show={true} size="xl">
        <Modal.Header style={{ backgroundColor: "white" }}>
          <Col>
            <h2>Transaction Details</h2>
          </Col>
          <Col xs={3} className="text-end">
            <ButtonWrapper
              buttonText="Close x"
              fullWidth={true}
              onClick={() => navigate("/reports")}
            />
          </Col>
        </Modal.Header>
        <Modal.Body>
          <Container>
            <div>Error loading transaction: {error.message}</div>
          </Container>
        </Modal.Body>
      </Modal>
    );
  }

  const invoiceColor = (status: string) => {
    switch (invoice?.status) {
      case "paid":
        return "green";
      case "pending":
        return "orange";
      case "failed":
        return "red";
      default:
        return "black";
    }
  };

  const formatter = new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",

    // These options are needed to round to whole numbers if that's what you want.
    //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    //maximumFractionDigits: 0, // (causes 2500.99 to be printed as $2,501)
  });

  return (
    <Modal show={true} size="xl">
      <Modal.Header style={{ backgroundColor: "white" }}>
        <Col>
          <h2>Transaction Details</h2>
        </Col>
        <Col xs={3} className="text-end">
          <ButtonWrapper
            buttonText="Close x"
            fullWidth={true}
            onClick={() => navigate("/reports")}
          />
        </Col>
      </Modal.Header>
      <Modal.Body>
        {transaction && (
          <Container>
            <h4>
              {transaction.startUtc && (
                <LocalDateTime date={transaction.startUtc} />
              )}{" "}
              - {transaction.siteName || "Unknown Site"}
            </h4>

            <Row>
              <Col>
                <label style={{ color: "#fe9015" }}>Type</label>
                <p>{transaction.type || "-"}</p>
              </Col>
              <Col>
                <label style={{ color: "#fe9015" }}>Duration</label>
                <p>
                  {transaction.endUtc && transaction.duration ? (
                    <ParkingDuration duration={transaction.duration} />
                  ) : (
                    transaction.startUtc && (
                      <ParkingDuration
                        duration={
                          (new Date().getTime() -
                            new Date(transaction.startUtc + "Z").getTime()) /
                          1000
                        }
                      />
                    )
                  )}
                </p>
              </Col>
              <Col>
                <label style={{ color: "#fe9015" }}>Cost</label>
                <p>
                  {transaction.amount
                    ? formatter.format(transaction.amount)
                    : "-"}
                </p>
              </Col>
              <Col>
                <label style={{ color: "#fe9015" }}>Status</label>
                <p>{transaction.status || "-"}</p>
              </Col>
            </Row>

            {invoice && (
              <Row>
                <Col>
                  <label style={{ color: "#fe9015" }}>Invoice</label>
                  <p>
                    {invoice.number || "-"}{" "}
                    <span style={{ color: invoiceColor(invoice.status || "") }}>
                      {invoice.status || "-"}
                    </span>
                    {invoice.url && (
                      <span
                        className="ms-4 btn btn-secondary"
                        onClick={() => window.open(invoice?.url, "_blank")}
                      >
                        Download
                      </span>
                    )}
                  </p>
                </Col>
              </Row>
            )}

            <h4>Events</h4>
            <table className="table sharepark-table">
              <thead>
                <tr>
                  <th>Date / Time</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {transaction.events?.map((e: TransactionEvent, i: number) => {
                  return (
                    <tr key={i}>
                      <td>
                        {e.createdUtc && <LocalDateTime date={e.createdUtc} />}
                      </td>
                      <td>{e.message || "-"}</td>
                    </tr>
                  );
                }) || (
                  <tr>
                    <td colSpan={2}>No events available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </Container>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default Reports;
